import { Fragment, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import {
    Amount,
    GeneralPricingMode,
    Product,
    ProductResult,
    Service,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { Divider, Grid, GridItem, Stack, Typography } from '@optimus-web/core';
import { FormCounterField, FormHiddenInput, LocalizedProperty, LocalizedText, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { SearchBar } from '@mews-commander/ui';
import { CurrencyInputField } from '@mews-commander/form';
import { IndexedProduct, sortByName, sortByOrdering, useFilteredProducts } from '../hooks/useFilteredProducts';
import { TooltippedAutomationIcon } from '../../../../routingRulesNew/components/AutomationIcon';
import { useGetServiceProductsCurrency } from '../hooks/useGetServiceProductsCurrency';
import { ProductsMovedByRoutingRuleResult } from '../hooks/useProductsMovedByRoutingRule';
import { useBillingAutomationOwnerName } from '../hooks/useBillingAutomationOwnerName';
import { useCurrencyFormat } from '../../../../../core/globalization/hooks/useCurrencyFormat';
import { AddProductFormValues, ProductOrdersField, ServiceOrderFormField } from './schema';
import { getTotalAmount } from './utils';
import { TotalBar } from './TotalBar';

const ROW_HEIGHT = '72px';

interface WithOverridablePrice {
    hasOverridablePrice: boolean;
}

export const ProductsSection = ({
    service,
    accountId,
    productsData,
    productsMovedByRoutingRule,
}: {
    service: Service;
    accountId: string;
    productsData: ProductResult;
    productsMovedByRoutingRule: Omit<ProductsMovedByRoutingRuleResult, 'banner'>;
}) => {
    const { data: exchangeRates } = useRepositoryQuery('currencyAcceptances', 'getAll', {});
    const { watch } = useFormContext<AddProductFormValues>();
    const formValues = watch();

    const { productsToDisplay, onSearchChange } = useFilteredProducts(
        productsData.products
            ?.filter((product: Product) => !product.isDefault && product.options.offerToEmployee)
            .map((p, index) => ({
                ...p,
                index,
            })) || []
    );

    const { hasOverridablePrice } = service.options;

    const { currency, hasSingleCurrency } = useGetServiceProductsCurrency(productsData.products || [], service);

    const sortedProductCategories = pipe(
        productsData.productCategories || [],
        array.sortBy([sortByOrdering, sortByName])
    );

    const categories = sortedProductCategories.map((category) => ({
        label: <LocalizedProperty property={category.names} />,
        products: productsToDisplay.filter((p) => p.productCategoryId === category.id),
        key: category.id,
    }));

    const productsWithoutCategory = productsToDisplay.filter((p) => !p.productCategoryId);

    const countSelected = (formValues.productOrders || []).reduce((acc, po) => acc + (po.count || 0), 0);
    const totalAmount = getTotalAmount({
        hasSingleCurrency,
        formValues,
        exchangeRates,
        serviceCurrency: formValues.currency,
    });

    // every service has 1 default product, that's why ` - 1`
    const visibleProductsCount = productsData.products ? productsData.products.length - 1 : 0;

    return (
        <Stack vertical spacing="300">
            {visibleProductsCount >= 5 ? <SearchBar onSearchChange={onSearchChange} /> : null}

            {categories.map(({ label, products, key }, index) =>
                products.length > 0 ? (
                    <Fragment key={key}>
                        <Category
                            products={products}
                            productsMovedByRoutingRule={productsMovedByRoutingRule}
                            label={label}
                            hasOverridablePrice={hasOverridablePrice}
                            accountId={accountId}
                        />
                        {index < categories.length - 1 || productsWithoutCategory.length ? <Divider /> : null}
                    </Fragment>
                ) : null
            )}

            {productsWithoutCategory.length > 0 ? (
                <Category
                    products={productsWithoutCategory}
                    productsMovedByRoutingRule={productsMovedByRoutingRule}
                    label={<LocalizedProperty property={service.names} />}
                    hasOverridablePrice={hasOverridablePrice}
                    accountId={accountId}
                />
            ) : null}

            <TotalBar
                currency={currency}
                totalAmount={totalAmount}
                hasSingleCurrency={hasSingleCurrency}
                count={countSelected}
                hasProducts={visibleProductsCount > 0}
            />
        </Stack>
    );
};

const Category = ({
    products,
    label,
    accountId,
    productsMovedByRoutingRule,
    hasOverridablePrice,
}: {
    products: IndexedProduct[];
    label: ReactNode;
    accountId: string;
    productsMovedByRoutingRule: Omit<ProductsMovedByRoutingRuleResult, 'banner'>;
} & WithOverridablePrice) => {
    const { productsIds, allProducts, routingRule } = productsMovedByRoutingRule;
    const ownedBy = useBillingAutomationOwnerName(accountId, routingRule);

    return (
        <Grid
            columnGap="200"
            rowGap="0"
            templateColumns="2fr 1fr 1fr"
            templateRows={`auto repeat(${products.length}, ${ROW_HEIGHT})`}
        >
            <GridRow
                title={
                    <Typography textStyle="bodyLargeStrong" as="div">
                        {label}
                    </Typography>
                }
                amount={
                    <Typography textStyle="bodyLargeStrong" as="div">
                        <LocalizedText textKey={TextKey.GeneralAmountPrice} />
                    </Typography>
                }
                count={
                    <Typography textStyle="bodyLargeStrong" as="div">
                        <LocalizedText textKey={TextKey.Quantity} />
                    </Typography>
                }
            />
            {products.map((product) => (
                <ProductConfig
                    key={product.id}
                    billingAutomationIcon={
                        (allProducts || productsIds.includes(product.id)) && (
                            <TooltippedAutomationIcon
                                tooltip={lt('AddServiceToReservationProductMovedByBillingAutomation', {
                                    ownedBy,
                                    billingAutomation: routingRule?.name ?? '',
                                })}
                            />
                        )
                    }
                    hasOverridablePrice={hasOverridablePrice}
                    {...product}
                />
            ))}
        </Grid>
    );
};

const getFieldName = (productOrderField: ProductOrdersField, index: number) =>
    `${ServiceOrderFormField.ProductOrders}[${index}].${productOrderField}`;

const ProductConfig = ({
    pricing,
    names,
    index,
    id,
    options,
    hasOverridablePrice,
    billingAutomationIcon,
}: IndexedProduct & WithOverridablePrice & { billingAutomationIcon: ReactNode }) => {
    const { grossValue, netValue, value, currency } = pricing.value as Amount;
    const defaultValue = (useCurrentEnterprise().pricing === GeneralPricingMode.Net ? netValue : grossValue) || value;

    return (
        <>
            <FormHiddenInput name={getFieldName(ProductOrdersField.ProductId, index)} defaultValue={id} />
            <FormHiddenInput name={getFieldName(ProductOrdersField.ProductCurrency, index)} defaultValue={currency} />
            <GridRow
                title={
                    <Stack itemAlignment="center" distribution="space-between">
                        <Typography textStyle="bodyLarge" as="div">
                            <LocalizedProperty property={names} />
                        </Typography>
                        {billingAutomationIcon}
                    </Stack>
                }
                count={
                    <FormCounterField
                        defaultValue={options.selectedByDefault ? 1 : 0}
                        size="medium"
                        name={getFieldName(ProductOrdersField.Count, index)}
                    />
                }
                amount={
                    <CurrencyField
                        defaultValue={defaultValue}
                        currency={currency}
                        name={getFieldName(ProductOrdersField.UnitCost, index)}
                        hasOverridablePrice={hasOverridablePrice}
                    />
                }
            />
        </>
    );
};

const GridRow = ({ title, count, amount }: { title: ReactNode; count?: ReactNode; amount?: ReactNode }) => (
    <>
        <GridItem alignSelf="center">{title}</GridItem>
        <GridItem alignSelf="center">{count}</GridItem>
        <GridItem alignSelf="center">{amount}</GridItem>
    </>
);

const CurrencyField = ({
    name,
    defaultValue,
    currency,
    hasOverridablePrice,
}: {
    name: string;
    defaultValue: number;
    currency: string;
} & WithOverridablePrice) => {
    const currencyLocalizationInfo = useCurrencyFormat(currency);

    return (
        <CurrencyInputField
            defaultValue={defaultValue}
            name={name}
            fixedDecimalScale
            readOnly={!hasOverridablePrice}
            {...currencyLocalizationInfo}
            precision={2}
        />
    );
};
